import * as React from 'react';
import {
  GetChannelLookupAPI,
  GetChannelByIdAPI,
  SaveChannelArchitectureAPI,
  SaveGradeArchitectureAPI,
  SaveMinutageArchitectureAPI,
} from 'api';
import CAGrading from './CAGrading';
import CAMinutage from './CAMinutage';
import CAProgrammes from './CAProgrammes';
import Header from 'shared/components/header/Header';
import { addDaysToDate, getMondayOfTheWeek } from 'utils';
import useStore from 'store/AccountStore';
import './ChannelArchitecture.css';
import { useCallbackPrompt } from 'components/_reusable/blocker/UsePromptComponent.ts';
import ConfirmLeaveModal from 'shared/components/confirm-leave-modal/ConfirmLeaveModal';
import { schedulesReducer } from './reducers/schedules.reducer';
import {
  gradingReducer,
  initialGradingState,
} from './reducers/grading.reducer';
import {
  initialMinutageState,
  minutageReducer,
} from './reducers/minutage.reducer';
import useNotification from 'store/NotificationStore';
import { calculateDates, getChannelSession } from 'utils';
import moment from 'moment';
import { getBlockName } from './utils/architecture.utils';
import OpSelect from 'op2mise-react-widgets/OpSelect/OpSelect';
import { ActionTypes } from './ChannelArchitecture.d';
import { getMondayZeroToSixSchedules } from './helper/getMondayZeroToSixSchedules';
import SchedulerHeaderTabs from './components/SchedulerHeaderTabs';

const ChannelArchitecture = () => {
  const { user, updateUser, updateChannel } = useStore((state) => state);
  const startDate = React.useMemo(() => getMondayOfTheWeek(new Date()), []);
  const endDate = React.useMemo(() => addDaysToDate(startDate, 7), [startDate]);

  const [activatedTab, setActivatedTab] = React.useState('Program');
  const [channelsList, setChannelsList] = React.useState([]);
  const [numberOfWeeks, setNumberOfWeeks] = React.useState(1);
  const [selectedChannel, setSelectedChannel] = React.useState(0);
  const [channelInfo, setChannelInfo] = React.useState({
    channelId: 0,
    channelName: null,
    timezone: null,
    timezoneID: null,
  });
  const [isDirty, setIsDirty] = React.useState(false);
  const { success, danger } = useNotification((state) => state);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    isDirty
  );
  const [schedules, dispatchSchedules] = React.useReducer(schedulesReducer, { programmes: [], previous: [] });
  const [showDirtyForm, setShowDirtyForm] = React.useState(false);
  const [next, setNext] = React.useState();
  const [grading, dispatchGrading] = React.useReducer(
    gradingReducer,
    initialGradingState,
  );
  const [minutage, dispatchMinutage] = React.useReducer(
    minutageReducer,
    initialMinutageState,
  );

  const tabOptions = ['Program', 'Grading', 'Minutage'];

  const getSession = (channels) => {
    const { channelId, tab } = user;
    var sessionChannel = getChannelSession(channelId, channels);
    handleSelectChannel(sessionChannel);

    if (tab && tabOptions.includes(tab)) {
      setActivatedTab(tab);
    } else {
      updateUser({ tab: tabOptions[0] });
    }
  };

  const getChannelsAPI = () => {
    GetChannelLookupAPI({
      onSuccess: (res) => {
        if (res.length > 0) {
          setChannelsList(res);
          getSession(res);
        }
      },
    });
  };

  const handleSchedulerOverlapCheck = (events) => {
    if (!Array.isArray(events) || events.length <= 1) return false;

    // Normalize and sort by StartTime
    const normalized = events
      .map(e => ({
        id: e.id,
        start: new Date(e.startTime).getTime(),
        end: new Date(e.endTime).getTime(),
      }))
      .sort((a, b) => a.start - b.start);

    // Compare each event to the next
    return normalized.some((event, i, arr) => {
      const next = arr[i + 1];
      return next && event.end > next.start;
    });
  };

  const handleSaveChannelArchitecture = React.useCallback(
    (events) => {
      const hasOverlaps = handleSchedulerOverlapCheck(events);
      if (hasOverlaps) {
        danger('Unable to save channel architecture. One or more blocks overlap with existing durations. Please review the affected weeks, days, start, or end times to resolve scheduling conflicts before saving.')
        if (showPrompt) cancelNavigation();
        setShowDirtyForm(false);
        return;
      }

      let blocks = [];

      if (events.length) {
        blocks = events
          .map((schedule) => {
            return {
              availableDuration: schedule?.availableDuration
                ? schedule.availableDuration
                : 0,
              blockName: getBlockName(schedule.dayOfWeek, schedule.startTime),
              blockReference: schedule?.blockReference,
              channelID: channelInfo.channelId,
              dayOfWeek: schedule.dayOfWeek,
              endTime: moment(schedule.endTime, 'HH:mm:ss').format('HH:mm:ss'),
              genre: schedule?.genre ? schedule.genre : '',
              layout: schedule?.layout,
              link: schedule?.link ?? false,
              maxCount: schedule?.maxCount ? schedule.maxCount : 0,
              seasonID: schedule?.seasonID
                ? schedule.seasonID.toString()
                : null,
              seriesID: schedule?.seriesID ? +schedule.seriesID : null,
              seriesName: schedule?.seriesName ? schedule.seriesName : null,
              sequential: false,
              startTime: moment(schedule.startTime, 'HH:mm:ss').format(
                'HH:mm:ss'
              ),
              titleGroupID: schedule?.titleGroupID
                ? +schedule.titleGroupID
                : null,
              titleGroupName: schedule?.titleGroupName
                ? schedule.titleGroupName
                : null,
              type: schedule?.type
                ? schedule.type === 'Title Group'
                  ? 'TitleGroup'
                  : schedule.type
                : 'Movie',
              week: schedule?.week,
              id: isNaN(schedule?.id) ? 0 : schedule.id,
            };
          })
          .filter((block) => block.week === 1);
      }

      SaveChannelArchitectureAPI({
        queryParams: {
          channelId: channelInfo.channelId,
          payload: { blocks: blocks },
        },
        onSuccess: (res) => {
          if (res) {
            setIsDirty(false);
            dispatchSchedules({ actionType: ActionTypes.CLEARHISTORY });
            if (showDirtyForm) {
              handleDiscardDirtyForm();
            }
            if (showPrompt) confirmNavigation();
            if (!showPrompt && !showDirtyForm) success('Successfully saved!');
          }
        },
      });
    },
    [channelInfo, showPrompt, showDirtyForm]
  );

  const handleSaveGradeArchitecture = React.useCallback(
    (grading) => {
      let blocks = [];

      if (grading.length) {
        blocks = grading.map((schedule) => {
          return {
            id: isNaN(schedule?.id ?? schedule?.Id) ? 0 : schedule.id ?? schedule.Id,
            channelID: channelInfo.channelId,
            dayOfWeek: schedule.dayOfWeek ?? schedule.DayOfWeek,
            startTime: moment(schedule.startTime ?? schedule.StartTime, 'HH:mm:ss').format(
              'HH:mm:ss'
            ),
            endTime: moment(schedule.endTime ?? schedule.EndTime, 'HH:mm:ss').format('HH:mm:ss'),
            grade: schedule?.grade ?? schedule?.Grade ?? 0,
            value: 0,
            miutage: 0,
          };
        });
      }
      SaveGradeArchitectureAPI({
        queryParams: {
          channelId: channelInfo.channelId,
          payload: { gradeMinutageArchitectures: blocks },
        },
        onSuccess: (res) => {
          dispatchGrading({ actionType: 'RESET_HISTORY' });
          setIsDirty(false);
          if (showDirtyForm) {
            handleDiscardDirtyForm();
          }
          if (showPrompt) confirmNavigation();
          if (!showPrompt && !showDirtyForm) success('Successfully saved!');
        },
      });
    },
    [channelInfo, dispatchGrading]
  );

  const handleSaveMinutageArchitecture = React.useCallback(
    (minutage) => {
      let blocks = [];

      if (minutage.length) {
        blocks = minutage.map((schedule) => {
          return {
            id: isNaN(schedule?.id) ? 0 : schedule.id,
            channelID: channelInfo.channelId,
            dayOfWeek: schedule.dayOfWeek,
            startTime: moment(schedule.startTime, 'HH:mm:ss').format(
              'HH:mm:ss'
            ),
            endTime: moment(schedule.endTime, 'HH:mm:ss').format('HH:mm:ss'),
            grade: 0,
            value: 0,
            minutage: schedule?.minutage ?? null,
          };
        });
      }
      SaveMinutageArchitectureAPI({
        queryParams: {
          channelId: channelInfo.channelId,
          payload: { minutageArchitectures: blocks },
        },
        onSuccess: (res) => {
          dispatchMinutage({ actionType: 'RESET_HISTORY' });
          setIsDirty(false);
          if (showDirtyForm) {
            handleDiscardDirtyForm();
          }
          if (showPrompt) confirmNavigation();
          if (!showPrompt && !showDirtyForm) success('Successfully saved!');
        },
      });
    },
    [channelInfo, dispatchMinutage]
  );

  const handleSaveDirty = React.useCallback(() => {
    switch (activatedTab) {
      case 'Program':
        handleSaveChannelArchitecture(schedules.programmes);
        break;
      case 'Grading':
        handleSaveGradeArchitecture(grading.current);
        break;
      case 'Minutage':
        handleSaveMinutageArchitecture(minutage.current);
        break;
    }
  }, [
    activatedTab,
    grading,
    minutage,
    showDirtyForm,
    showPrompt,
    schedules.programmes,
  ]);

  const handleDiscardDirtyForm = () => {
    setIsDirty(false);
    if (next.type === 'tab') handleTabChange(next.value, false);
    if (next.type == 'channel') {
      const args = next.value;
      setSelectedChannel(args.channelId);
      setChannelInfo(args);
    }
    setShowDirtyForm(false);
  };

  const handleTabChange = (tab, dirty) => {
    if (dirty) {
      setNext({ type: 'tab', value: tab });
      setShowDirtyForm(true);
    } else {
      setActivatedTab(tab);
      updateUser({ tab: tab });
    }
  };

  const handleGetEventDate = React.useCallback(
    (args, attribute) => {
      const tempDate = calculateDates(
        args.dayOfWeek,
        args.week ?? 1,
        args.startTime,
        args.endTime,
        { startDate: startDate, endDate: endDate }
      )[attribute];
      return tempDate;
    },
    [startDate, endDate]
  );

  const handleConstructGridData = React.useCallback(
    (data, removeDuplicates) => {
      return getMondayZeroToSixSchedules(
        [...data],
        { startDate, endDate },
        user?.clockType === 'Broadcast',
        removeDuplicates
      );
    },
    [user, startDate, endDate]
  );

  const headerComponent = React.useMemo(() => {
    return (
      <SchedulerHeaderTabs
        activatedTab={activatedTab}
        onTabChange={(tab) => handleTabChange(tab, isDirty)}
      />
    );
  }, [activatedTab, isDirty]);

  const programmeProps = React.useMemo(() => {
    return {
      numberOfWeeks,
      setNumberOfWeeks,
      startDate,
      endDate,
      handleGetEventDate,
      isDirty,
      setIsDirty,
      schedules,
      dispatchSchedules,
      handleSaveChannelArchitecture,
      channelInfo,
      handleConstructGridData,
      headerComponent,
    };
  }, [
    startDate,
    endDate,
    selectedChannel,
    numberOfWeeks,
    isDirty,
    schedules,
    channelInfo,
    handleGetEventDate,
    handleConstructGridData,
    headerComponent,
  ]);

  const gradingProps = React.useMemo(() => {
    return {
      channelInfo,
      startDate,
      endDate,
      handleGetEventDate,
      isDirty,
      setIsDirty,
      grading: grading.current,
      canUndo: grading.previous.length > 0,
      undoHistoryLength: grading.previous.length,
      dispatchGrading,
      handleSaveGradeArchitecture,
      handleConstructGridData,
      headerComponent,
    };
  }, [
    isDirty,
    channelInfo,
    handleGetEventDate,
    handleConstructGridData,
    grading,
    dispatchGrading,
    headerComponent
  ]);

  const minutageProps = React.useMemo(() => {
    return {
      channelInfo,
      startDate,
      endDate,
      handleGetEventDate,
      isDirty,
      setIsDirty,
      minutage: minutage.current,
      canUndo: minutage.previous.length > 0,
      undoHistoryLength: minutage.previous.length,
      dispatchMinutage,
      handleSaveMinutageArchitecture,
      handleConstructGridData,
      headerComponent,
    };
  }, [
    isDirty,
    channelInfo,
    handleGetEventDate,
    handleConstructGridData,
    minutage,
    dispatchMinutage,
    headerComponent,
  ]);

  const activeCAComponent = React.useMemo(
    () =>
      activatedTab === 'Grading' ? (
        <CAGrading {...gradingProps} />
      ) : activatedTab === 'Minutage' ? (
        <CAMinutage {...minutageProps} />
      ) : (
        <CAProgrammes {...programmeProps} />
      ),
    [
      activatedTab,
      endDate,
      channelInfo,
      numberOfWeeks,
      schedules,
      isDirty,
      gradingProps,
      minutageProps,
      programmeProps,
    ]
  );

  const handleSelectChannel = (args) => {
    if (isDirty) {
      setNext({ type: 'channel', value: args });
      setShowDirtyForm(true);
    } else {
      setSelectedChannel(args.channelId);
      setChannelInfo(args);
      // Save to session
      updateChannel(args);
    }
  };

  React.useEffect(() => {
    getChannelsAPI();
  }, []);

  React.useEffect(() => {
    if (selectedChannel) {
      GetChannelByIdAPI({
        queryParams: selectedChannel,
        onSuccess: (res) => {
          setChannelInfo({
            channelName: res.channelName,
            channelId: res.channelId,
            timezone: res.timeZoneName,
            timeoneID: res.timeZoneID,
          });
          // Save to session
          updateChannel(res);
        },
      });
    }
  }, [selectedChannel]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        title={'Architecture'}
        childComponent={
          <div
            className="d-flex align-items-center gap-4"
            style={{ marginBottom: '5px' }}
          >
            {/* Channels Selection/Dropdown */}
            <div className="architecture-banner-channel-selector">
              <div>
                {/** Re-locate this re-usable component to `op2mise-react-widgets */}
                <OpSelect
                  allowFiltering={true}
                  dataSource={
                    channelsList
                      ? channelsList.map((item) => ({
                          id: item.channelId,
                          name: item.channelName,
                          ...item,
                        }))
                      : null
                  }
                  fields={{ text: 'name', value: 'id' }}
                  filterBarPlaceholder="Search"
                  onChange={(e) => {
                    handleSelectChannel(e.target.itemData);
                  }}
                  placeholder={'Please select a channel'}
                  value={selectedChannel}
                  width={150}
                />
              </div>
            </div>
          </div>
        }
      />
      <div className="architecture-content">
        {activeCAComponent}
      </div>
      <ConfirmLeaveModal
        show={showPrompt}
        onSave={() => {
          handleSaveDirty();
        }}
        onDiscard={confirmNavigation}
        onHide={cancelNavigation}
      />

      <ConfirmLeaveModal
        show={showDirtyForm}
        onSave={() => {
          handleSaveDirty();
        }}
        onDiscard={handleDiscardDirtyForm}
        onHide={() => setShowDirtyForm(false)}
      />
    </div>
  );
};

export default ChannelArchitecture;
